import { Button, Typography } from "@mui/material";
import clsx from "clsx";
import _ from "lodash";
import pluralize from "pluralize";
import React, { useMemo, useState } from "react";
import { dataMapPendingDisclosedPicGroupIds } from "../../../common/service/resources/data-map/dataMapPending";
import { useDataMapDisclosedPICGroups } from "../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../common/service/server/Types";
import { UseDisclosuresForCollectionGroups } from "../../hooks/useDisclosuresForCollectionGroups";
import { VENDOR_KEY_TRUEVAULT } from "../../types/Vendor";
import { CalloutVariant } from "../Callout";
import { DataRecipientCollapseCard } from "../DataRecipientCollapseCard";
import { RemoveVendorDialog } from "../dialog/RemoveVendorDialog";
import { DisableableButton } from "../Disableable";
import { DataRecipientDisclosureCheckboxes } from "./DataRecipientDisclosureCheckboxes";

type DataRecipientCollectionCardsProps = {
  dataMap: DataMapDto;
  disclosureData: UseDisclosuresForCollectionGroups;
  groups: CollectionGroupDetailsDto[];
  context: CollectionContext[];
  showPendingRecipients?: boolean;
  showSensitiveCategories?: boolean;
  selectorPrefix?: string;
};

export const DataRecipientDisclosureCards: React.FC<DataRecipientCollectionCardsProps> = ({
  dataMap,
  disclosureData,
  groups,
  context,
  showPendingRecipients = false,
  showSensitiveCategories = false,
  selectorPrefix = "",
}) => {
  const recipients = showPendingRecipients
    ? disclosureData.dataRecipients
    : disclosureData.dataRecipientsWithDisclosedPIC;
  const [expanded, setExpanded] = useState<null | string>(recipients[0]?.vendorId);
  const handleExpand = (vendorId: UUIDString) => {
    setExpanded(vendorId);
    setTimeout(
      () => window.polaris.scrollSelectorIntoView(`#dr${selectorPrefix}-${vendorId}`),
      300,
    );
  };

  return (
    <>
      {recipients.map((r, idx) => {
        const next = idx != recipients.length - 1 ? recipients[idx + 1] : null;
        return (
          <CardWrapper
            key={r.vendorId}
            dataMap={dataMap}
            disclosureData={disclosureData}
            r={r}
            next={next}
            expanded={expanded == r.vendorId}
            groups={groups}
            context={context}
            onExpand={handleExpand}
            showSensitiveCategories={showSensitiveCategories}
            selectorPrefix={selectorPrefix}
          />
        );
      })}
    </>
  );
};

type CardWrapperProps = {
  dataMap: DataMapDto;
  disclosureData: UseDisclosuresForCollectionGroups;
  r: OrganizationDataRecipientDto;
  expanded: boolean;
  next: OrganizationDataRecipientDto | null;
  groups: CollectionGroupDetailsDto[];
  context: CollectionContext[];
  onExpand: (vendorId: UUIDString) => void;
  showSensitiveCategories?: boolean;
  selectorPrefix?: string;
};
const CardWrapper: React.FC<CardWrapperProps> = ({
  dataMap,
  disclosureData,
  r,
  expanded,
  next,
  groups,
  context,
  onExpand,
  showSensitiveCategories,
  selectorPrefix = "",
}) => {
  const disclosedGroups = useDataMapDisclosedPICGroups(dataMap, {
    vendorId: r.vendorId,
    collectionGroupId: groups.map((g) => g.id),
  });
  const disclosedCount = disclosedGroups.length;
  const disclosuresLabel = pluralize("Disclosures", disclosedCount, true);
  const [removeVendorOpen, setRemoveVendorOpen] = useState(false);
  const openRemoveVendor = () => setRemoveVendorOpen(true);
  const closeRemoveVendor = () => setRemoveVendorOpen(false);

  return (
    <div id={`dr${selectorPrefix}-${r.vendorId}`} key={r.vendorId}>
      <DataRecipientCollapseCard
        recipient={r}
        collapsed={!expanded}
        variant={CalloutVariant.LightPurpleBordered}
        onExpand={() => onExpand(r.vendorId)}
        headerContent={
          <Typography
            variant="caption"
            className={clsx({
              "text-error": disclosedCount === 0,
              "text-muted": disclosedCount > 0,
            })}
          >
            {disclosuresLabel} Selected
          </Typography>
        }
      >
        <div>
          <CheckboxesWrapper
            dataMap={dataMap}
            disclosureData={disclosureData}
            recipient={r}
            key={r.id}
            groups={groups}
            showSensitiveCategories={showSensitiveCategories}
          />
        </div>

        {Boolean(next) && (
          <Button
            className="mt-lg mr-lg"
            variant="contained"
            size="small"
            onClick={() => onExpand(next!.vendorId)}
          >
            Next Vendor
          </Button>
        )}

        <DisableableButton
          className="mt-lg"
          color="secondary"
          variant="text"
          size="small"
          onClick={openRemoveVendor}
          disabled={r.vendorKey == VENDOR_KEY_TRUEVAULT}
          disabledTooltip="You cannot remove the TrueVault vendor"
        >
          Remove
        </DisableableButton>

        <RemoveVendorDialog
          open={removeVendorOpen}
          onClose={closeRemoveVendor}
          vendorId={r.vendorId}
          vendorName={r.name}
          collectionContext={context}
        />
      </DataRecipientCollapseCard>
    </div>
  );
};

type CheckboxesWrapperProps = {
  dataMap: DataMapDto;
  disclosureData: UseDisclosuresForCollectionGroups;
  recipient: OrganizationDataRecipientDto;
  groups: CollectionGroupDetailsDto[];
  showSensitiveCategories?: boolean;
};
const CheckboxesWrapper: React.FC<CheckboxesWrapperProps> = ({
  dataMap,
  disclosureData,
  recipient,
  groups,
  showSensitiveCategories,
}) => {
  const selectedPicGroups = useDataMapDisclosedPICGroups(dataMap, {
    vendorId: recipient.vendorId,
    collectionGroupId: groups.map((g) => g.id),
  });
  const selectedPicGroupIds = _.uniq(selectedPicGroups.map((g) => g.id));
  const pendingPicGroupIds = useMemo(
    () =>
      dataMapPendingDisclosedPicGroupIds(dataMap, {
        vendorId: recipient.vendorId,
        collectionGroupId: groups.map((g) => g.id),
      }),
    [dataMap, recipient.vendorId, groups],
  );
  if (!dataMap) {
    return null;
  }
  return (
    <DataRecipientDisclosureCheckboxes
      dataRecipient={recipient}
      collectionGroups={groups}
      collectedPicGroups={disclosureData.collectedPICGroups}
      selected={selectedPicGroupIds}
      pending={pendingPicGroupIds}
      dataMap={dataMap}
      showCollectedRecommendations
      updateDisclosure={disclosureData.actions.updateDisclosure}
      showSensitiveCategories={showSensitiveCategories}
    />
  );
};
